import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { oapi, todayYmd } from '@/lib/officialsApi';
import { ADJ_LABEL, Field } from './PayBits';

const blank = (t) => ({ official_id: t?.official_id || '', adj_type: 'travel', description: '', amount: '', date: todayYmd(), event_id: '', approve_now: true });

export const AdjustmentDialog = ({ target, orgId, currency, officials, competitions, onClose, onDone }) => {
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) setForm(blank(target)); }, [target]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      await oapi('/officials/payments/adjustments', { method: 'POST', orgId,
        body: { ...form, event_id: form.event_id || null, related_line_id: target.related_line_id || null } });
      toast.success(form.approve_now ? 'Adjustment added and approved' : 'Adjustment added · waiting for approval');
      onDone();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const deduction = form.adj_type === 'deduction';
  const valid = form.official_id && form.description.trim() && Number(form.amount) > 0;
  return (
    <Dialog open={!!target} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="adjustment-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#00247D]">Add adjustment</DialogTitle>
          <DialogDescription>Travel, bonuses, extra fees or deductions. Approved and paid amounts are never edited - adjustments keep the history clean.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Official">
            <Select value={form.official_id} onValueChange={v => set('official_id', v)} disabled={!!target?.official_id}>
              <SelectTrigger data-testid="adjustment-official"><SelectValue placeholder="Choose an official" /></SelectTrigger>
              <SelectContent>{officials.map(o => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <Select value={form.adj_type} onValueChange={v => set('adj_type', v)}>
                <SelectTrigger data-testid="adjustment-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ADJ_LABEL).filter(([k]) => k !== 'rate_adjustment').map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={`${deduction ? 'Deduct' : 'Amount'} (${currency})`}>
              <Input type="number" min="0" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} data-testid="adjustment-amount"
                className={deduction ? 'text-red-600' : ''} />
            </Field>
          </div>
          {deduction && <p className="text-xs text-red-600 -mt-1">Deductions reduce what this official is owed.</p>}
          <Field label="Description"><Input value={form.description} onChange={e => set('description', e.target.value)} placeholder="e.g. Fuel for away tournament" data-testid="adjustment-description" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date"><Input type="date" value={form.date} onChange={e => set('date', e.target.value)} data-testid="adjustment-date" /></Field>
            <Field label="League (optional)">
              <Select value={form.event_id || 'none'} onValueChange={v => set('event_id', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="adjustment-league"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Not tied to a league</SelectItem>{competitions.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Switch checked={form.approve_now} onCheckedChange={v => set('approve_now', v)} data-testid="adjustment-approve-now" />Approve now (adds to the payable balance)
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !valid} onClick={submit} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="adjustment-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Add adjustment
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
